import React, { useEffect, useState } from 'react';
import { StarIcon, ArrowRightIcon, LockClosedIcon } from '@heroicons/react/24/outline'; // Replaced ClockIcon with ArrowRightIcon for action
import axios from 'axios';
import baseUrl from '../../config';
import { useNavigate } from 'react-router-dom';

const SuperSkillsUnlocked = () => {
  const [unlockedBatches, setUnlockedBatches] = useState([]);
  const [lockedBatches, setLockedBatches] = useState([]); // Keep for potential future display or logic
  const navigate = useNavigate(); // Initialize navigate hook

  const getBatchImage = (batchId) => {
    try {
      return require(`../../Assets/batch/${batchId}.png`);
    } catch (error) {
      // Fallback for missing images – a generic icon or placeholder
      return `https://api.dicebear.com/7.x/shapes/svg?seed=${batchId}&colors=emerald,yellow,indigo`; // Fun placeholder!
    }
  };

  useEffect(() => {
    const user = localStorage.getItem('user');
    let parsedUser = null;
    try {
      parsedUser = JSON.parse(user);
    } catch (e) {
      console.error("Failed to parse user from localStorage:", e);
      return;
    }

    if (!parsedUser || !parsedUser.id) {
      console.warn("User ID not found. Cannot fetch achievements.");
      return;
    }

    const fetchStudentAchievements = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.warn("Authentication token not found. Cannot fetch achievements.");
          return;
        }

        const response = await axios.get(`${baseUrl}api/students/achievment/${parsedUser.id}`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data) {
          // Take only the most recent few for a compact homepage display
          const unlocked = (response.data.unlockedBatches || []).slice(0, 3); // Show max 3 recent badges
          const locked = response.data.lockedBatches || [];
          setUnlockedBatches(unlocked);
          setLockedBatches(locked); // Still keep for potential "view all" or backend logic
        }
      } catch (error) {
        console.error('Error fetching student achievements:', error);
      }
    };

    fetchStudentAchievements();
  }, [baseUrl]);

  // Example: Calculate a dynamic progress percentage (replace with actual logic)
  const userOverallProgress = unlockedBatches.length > 0 ? (unlockedBatches.length / 5) * 100 : 0; // Example: 5 total badges achievable

  return (
    <div className="bg-white rounded-2xl p-6 shadow-glass border-b-4 border-emerald-600 transition-all duration-300 hover:shadow-xl hover:scale-[1.01]">
      {/* Background radial gradient for modern touch */}
      <div className="absolute -top-10 -right-10 w-40 h-40 bg-emerald-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
      <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-yellow-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>

      <div className="flex items-center justify-between mb-4 relative z-10">
        <h2 className="text-3xl font-extrabold text-black flex items-center drop-shadow-sm">
          <StarIcon className="w-8 h-8 mr-2 text-emerald-500 animate-bounce-slow" /> {/* Softer bounce */}
          Super Skills
        </h2>
        {/* Potentially a "View All" link here */}
        <button
                  onClick={() => navigate('/achivement')} // Navigate to the achievements page

        className="text-emerald-600 hover:text-emerald-800 flex items-center text-sm font-semibold transition-colors">
          View All <ArrowRightIcon className="w-4 h-4 ml-1" />
        </button>
      </div>

      <p className="text-md text-gray-700 mb-5 relative z-10">
        You're unlocking new powers! See your recent achievements.
      </p>

      {/* Badges Section */}
      <div className="mb-6 relative z-10">
        <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
          <span className="bg-emerald-200 text-emerald-800 rounded-full px-3 py-1 text-xs mr-2">NEW</span> Recent Badges
        </h3>
        <div className="flex flex-wrap gap-4 justify-center sm:justify-start">
          {unlockedBatches.length > 0 ? (
            unlockedBatches.map((badge) => (
              <div key={badge._id} className="relative group cursor-pointer">
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg border-4 border-emerald-400 transform group-hover:scale-110 transition-transform duration-200 overflow-hidden
                  // Subtle glow on hover
                  before:content-[''] before:absolute before:inset-0 before:rounded-full before:p-0.5 before:bg-gradient-to-r before:from-emerald-300 before:via-lime-300 before:to-emerald-300 before:opacity-0 group-hover:before:opacity-100 before:animate-spin-slow`}
                >
                  <img
                    src={getBatchImage(badge.batchId)}
                    alt={badge.name}
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
                {/* Tooltip for badge name */}
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-20">
                  {badge.name}
                </span>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-sm italic">No badges unlocked yet. Keep learning!</p>
          )}
        </div>
      </div>

      {/* Progress Bar Section */}
      <div className="relative z-10">
        <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
          <span className="bg-indigo-200 text-indigo-800 rounded-full px-3 py-1 text-xs mr-2">GO!</span> Your Progress
        </h3>
        <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden shadow-inner">
          <div
            className="bg-gradient-to-r from-emerald-400 via-yellow-400 to-indigo-400 h-full rounded-full transition-all duration-1000 ease-out flex items-center justify-end pr-2 text-white text-sm font-semibold"
            style={{ width: `${userOverallProgress}%` }}
          >
            {userOverallProgress.toFixed(0)}%
          </div>
        </div>
        <p className="text-center text-gray-600 text-xs mt-2">
          You're on track to become a true master!
        </p>
      </div>
    </div>
  );
};

export default SuperSkillsUnlocked;